const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const API_TIMEOUT = 60000; // 60 seconds for LLM responses

/**
 * Chat request payload
 */
export interface ChatRequest {
    chatId: string;
    message: string;
    route?: string;
}

/**
 * Chat response from API
 */
export interface ChatResponse {
    output: string;
}

/**
 * Send a chat message to the API
 * Includes timeout handling and error messages
 */
export async function sendMessage(request: ChatRequest): Promise<ChatResponse> {
    // Legacy support or single-shot
    let fullText = '';
    await sendMessageStream(request, (chunk) => {
        fullText += chunk;
    });
    return { output: fullText };
}

/**
 * Send a chat message and stream the response
 */
export async function sendMessageStream(
    request: ChatRequest,
    onChunk: (text: string) => void
): Promise<void> {
    const controller = new AbortController();
    // Timeout applied to initial connection, not duration of stream
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
        const response = await fetch(`${API_BASE_URL}/api/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.output || `Erro ${response.status}: ${response.statusText}`
            );
        }

        // Check content type to distinguish between JSON error/block and text stream
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            onChunk(data.output);
            return;
        }

        if (!response.body) {
            throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const text = decoder.decode(value, { stream: true });
            onChunk(text);
        }

    } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof Error) {
            if (error.name === 'AbortError') {
                throw new Error('A consulta demorou mais do que o esperado.');
            }
            throw error;
        }

        throw new Error('Erro desconhecido ao consultar o Oráculo.');
    }
}

/**
 * Get or create a chat ID from session storage
 */
export function getChatId(): string {
    let chatId = sessionStorage.getItem('chatId');
    if (!chatId) {
        chatId = 'chat_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('chatId', chatId);
    }
    return chatId;
}
